'use client';

import {
  SocialShareBar,
  SocialShareBarProps
} from '@/components/social-share/social-share-bar';
import { ButtonProps, ClickAwayListener, Grow, Popper } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Paper from '@mui/material/Paper';
import { SxProps, Theme } from '@mui/material/styles';
import { useFeatureGate } from '@statsig/react-bindings';
import {
  bindDialog,
  bindPopper,
  bindTrigger,
  usePopupState
} from 'material-ui-popup-state/hooks';
import { ReactNode, useEffect } from 'react';
import { ShareButton } from './share-button';

export type SocialShareButtonDialogProps = SocialShareBarProps & {
  children(props: ButtonProps): ReactNode;
};

export function SocialShareDialogTrigger({
  children,
  ...props
}: SocialShareButtonDialogProps) {
  const isLearnSharingEnabled = useFeatureGate('learn_sharing')?.value;
  const popupState = usePopupState({
    variant: isLearnSharingEnabled ? 'popper' : 'dialog'
  });

  useEffect(() => {
    if (popupState.isOpen) {
      rudderanalytics.track('Click', {
        element_id: 'social-share-button',
        type: 'BUTTON'
      });
    }
  }, [popupState.isOpen]);

  return (
    <>
      {isLearnSharingEnabled ? (
        <Popper
          {...bindPopper(popupState)}
          sx={{ zIndex: 'modal' }}
          placement="bottom-end"
          transition
        >
          {({ TransitionProps }) => (
            <Grow
              {...TransitionProps}
              timeout={350}
              style={{
                transformOrigin: 'right top'
              }}
            >
              <Paper elevation={2} sx={{ px: 1, py: 1.5, minWidth: 160 }}>
                <ClickAwayListener onClickAway={() => popupState.close()}>
                  <div>
                    <SocialShareBar
                      {...props}
                      orientation="vertical"
                      hideCopyButton={false}
                      hideTitle
                      hideCopyInput
                      iconSize={24}
                    />
                  </div>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      ) : (
        <Dialog maxWidth="sm" fullWidth {...bindDialog(popupState)}>
          <DialogContent>
            <SocialShareBar {...props} />
          </DialogContent>
        </Dialog>
      )}
      {children(bindTrigger(popupState))}
    </>
  );
}

export type SocialShareButtonProps = SocialShareBarProps &
  ButtonProps & {
    sx?: SxProps<Theme>;
    isTextButton?: boolean;
    isOutlinedButton?: boolean;
  };

export function SocialShareButton({
  sx,
  isTextButton,
  isOutlinedButton,
  ...socialShareBarProps
}: SocialShareButtonProps) {
  return (
    <SocialShareDialogTrigger {...socialShareBarProps}>
      {(props) => (
        <ShareButton
          isTextButton={isTextButton}
          isOutlinedButton={isOutlinedButton}
          sx={sx}
          {...props}
          className="social-share-button"
        />
      )}
    </SocialShareDialogTrigger>
  );
}
